import { useState } from 'react';
import { Box, Link, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useFormContext, useWatch } from 'react-hook-form';

import { Alert, theme } from '@theguarantors/tg-package-material-ui';

import { SectionHeading } from '@/features/LeaseDetails';
import { InfoBanner } from '@/components/InfoBanner';

import { HelpfulTipsDialog } from './content/helpful-tips.dialog';
import { InviteSmbStateCoverageInfoBanner } from './content/invite-smb-state-coverage-info-banner';

// Screening-section chrome that the engine can't express declaratively: the
// section heading (rendered via the shared `SectionHeading` so its title/subtitle
// match every other section), the helpful-tips modal, the lock banner, the
// deposit-coverage warning, and the PMI helper. Plugged into `section.slots` by
// the preset; reuses the legacy `HelpfulTipsDialog` so OFF and ON paths share
// one modal.

const responsiveBody = {
  fontSize: '16px',
  [theme.breakpoints.down('md')]: { fontSize: '14px', lineHeight: '22px' },
} as const;

const RequiredSelectOne = () => (
  <Typography
    mt={4}
    variant="body2"
    fontWeight="350"
    mb="12px"
    letterSpacing={0}
    sx={responsiveBody}
  >
    Select one{' '}
    <Box component="span" sx={{ color: theme.palette.red[600] }}>
      *
    </Box>
  </Typography>
);

type SmbScreeningIntroProps = {
  showStateCoverageInfoBanner?: boolean;
};

const SmbScreeningIntro = ({ showStateCoverageInfoBanner = false }: SmbScreeningIntroProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const subtitle = (
    <>
      This corresponds to the maximum amount of rent loss you can claim. For example, if you select
      3 months of coverage and your rent is $2,000, you can claim up to $6,000 of rent loss.
      <Box component="span" sx={{ display: 'block', mt: 2 }}>
        If you need help deciding,{' '}
        <Link
          onClick={() => setIsModalOpen(true)}
          sx={{
            textTransform: 'none',
            cursor: 'pointer',
            borderBottom: `2px solid ${theme.palette.midnightGrey[700]}`,
          }}
        >
          review our helpful tips.
        </Link>
      </Box>
    </>
  );

  return (
    <>
      <SectionHeading title="Select your maximum coverage limit" subtitle={subtitle} />
      {showStateCoverageInfoBanner && <InviteSmbStateCoverageInfoBanner />}
      <RequiredSelectOne />
      <HelpfulTipsDialog isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

const InstitutionalScreeningIntro = () => {
  const { control } = useFormContext();
  const address = useWatch({ control, name: 'address' });
  const isLocked = !address?.monolith_building_id;

  return (
    <>
      <SectionHeading
        title="Screening result"
        subtitle="Based on your review of this applicant at this time, select the appropriate screening result. This can be changed later, but it will impact the quoted premium."
      />
      {isLocked && (
        <InfoBanner icon={<LockOutlinedIcon fontSize="inherit" />} sx={{ mt: 1 }}>
          Please complete the lease information above first
        </InfoBanner>
      )}
      <RequiredSelectOne />
    </>
  );
};

type InviteScreeningIntroProps = {
  isSmbInvite: boolean;
  showStateCoverageInfoBanner?: boolean;
};

export const InviteScreeningIntro = ({
  isSmbInvite,
  showStateCoverageInfoBanner = false,
}: InviteScreeningIntroProps) =>
  isSmbInvite ? (
    <SmbScreeningIntro showStateCoverageInfoBanner={showStateCoverageInfoBanner} />
  ) : (
    <InstitutionalScreeningIntro />
  );

// SMB (non-PMI) deposit-coverage warning, shown when the switch is on.
export const InviteDepositCoverageWarning = () => {
  const { control } = useFormContext();
  const includeDepositCoverage = useWatch({ control, name: 'includeDepositCoverage' });
  if (!includeDepositCoverage) return null;

  return (
    <Alert severity="warning">
      <Typography variant="body2" mb={1} sx={responsiveBody}>
        Please note the following if you select this option:
      </Typography>
      <Box component="ul" sx={{ margin: 0, paddingLeft: 3 }}>
        <li>
          <Typography variant="body2" sx={responsiveBody}>
            Deposit Coverage is intended for landlords who do not expect to hold a cash security
            deposit
          </Typography>
        </li>
        <li>
          <Typography variant="body2" sx={responsiveBody}>
            The added coverage may raise renter's quote by up to 30%
          </Typography>
        </li>
        <li>
          <Typography variant="body2" sx={responsiveBody}>
            The likelihood of a renter being approved may be lower
          </Typography>
        </li>
      </Box>
    </Alert>
  );
};

// PMI helper line shown under the customize-coverage checkbox.
export const InvitePmiCoverageHelper = () => (
  <Typography variant="helpertext">
    * Policy price paid by the renter is determined by gross rent, the length of coverage, and a
    renter's risk profile, decided by the landlord.
  </Typography>
);
